import { useState } from "react";
import {
    Box,
    FileDropzone,
    FileInput,
    FileTokenGroup,
    FileTokenGroupProps,
    SpaceBetween,
} from "@amzn/awsui-components-react";
import { CustomFileFormFieldProps } from "interfaces";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";
import { validateFiles, ACCEPTED_FORMATS_DISPLAY } from "common/utils/file-validation";

const getStringPath: string = "common.component.customForms.customFileDropzone";
const getStringPathList: string = `${getStringPath}.fileList`;

/**
 * A FormField that provides a file dropzone input with file type validation.
 * Validates files on selection (both file picker and drag-and-drop) and shows
 * inline error feedback when unsupported file types are detected.
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 * @param {string} errorText - Error message to display (from parent form validation)
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} [testId] - Test identifier for the component
 * @param {string} [accept] - File types to accept
 * @param {boolean} [multiple] - Whether multiple files are allowed
 *
 * @returns {JSX.Element} A FormField component with a FileDropzone
 */
const CustomFileDropzone = ({
    label,
    errorText,
    warningText,
    description,
    info,
    value,
    setValue,
    testId,
    accept,
    multiple,
    touchpoint,
}: CustomFileFormFieldProps) => {
    const [rejectionError, setRejectionError] = useState<string>("");

    const filterFiles = (indexToRemove: number) => {
        return value.filter((_, index) => index !== indexToRemove);
    };

    const trackerId = useTrackerId();

    /**
     * Validates incoming files and adds only accepted ones to the form state.
     * Rejected files trigger an inline error message listing the rejected filenames.
     */
    const onChange = ({ detail }: { detail: { value: File[] } }) => {
        const { accepted, rejectedByType, rejectedBySize } = validateFiles(detail.value);

        const errors: string[] = [];

        if (rejectedByType.length > 0) {
            const rejectedNames = rejectedByType.map((file) => file.name).join(", ");
            errors.push(
                getString(`${getStringPath}.unsupportedFileType`, undefined, {
                    fileNames: rejectedNames,
                    acceptedFormats: ACCEPTED_FORMATS_DISPLAY,
                })
            );
        }

        if (rejectedBySize.length > 0) {
            const rejectedNames = rejectedBySize.map((file) => file.name).join(", ");
            errors.push(
                getString(`${getStringPath}.fileTooLarge`, undefined, {
                    fileNames: rejectedNames,
                })
            );
        }

        setRejectionError(errors.join(" "));

        if (accepted.length > 0) {
            setValue([...value, ...accepted]);
        }

        const eventData: Record<string, unknown> = {
            action: "file-change",
            files: detail.value,
            rejectedByType: rejectedByType.length,
            rejectedBySize: rejectedBySize.length,
        };

        trackCustomEvent(eventData, touchpoint, trackerId);
    };

    /**
     * Track custom uxm event for file removal
     */
    const onDismiss = ({ detail }: { detail: FileTokenGroupProps.DismissDetail }) => {
        setValue(filterFiles(detail.fileIndex));
        setRejectionError("");

        const eventData: Record<string, unknown> = {
            action: "file-removal",
        };

        trackCustomEvent(eventData, touchpoint, trackerId);
    };

    const displayError = errorText || rejectionError;

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={displayError}
            warningText={warningText}
            description={description}
            info={info}
            input={
                <SpaceBetween size="l">
                    <FileDropzone onChange={onChange}>
                        <SpaceBetween size="xxs" alignItems="center">
                            <Box color="inherit">{getString(`${getStringPath}.dropFiles`)}</Box>
                            <FileInput value={[]} onChange={onChange} accept={accept} multiple={multiple}>
                                {getString(`${getStringPath}.chooseFile`)}
                            </FileInput>
                            <Box variant="small" color="text-body-secondary">
                                {getString(`${getStringPath}.acceptedFormats`, undefined, {
                                    formats: ACCEPTED_FORMATS_DISPLAY,
                                })}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                                {getString(`${getStringPath}.maxFileSize`)}
                            </Box>
                        </SpaceBetween>
                    </FileDropzone>
                    <FileTokenGroup
                        items={value.map((file) => ({ file }))}
                        onDismiss={onDismiss}
                        showFileSize
                        showFileLastModified
                        showFileThumbnail
                        i18nStrings={{
                            removeFileAriaLabel: () => getString(`${getStringPathList}.removeList`),
                            limitShowFewer: getString(`${getStringPathList}.showLess`),
                            limitShowMore: getString(`${getStringPathList}.showMore`),
                        }}
                    />
                </SpaceBetween>
            }
        />
    );
};

export default CustomFileDropzone;
